// src/api/services/nations.service.ts

import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

import type {
  Nation,
  CreateNationRequest,
  UpdateNationRequest,
  EliminateNationRequest,
  Pagination,
} from "@/types/api.types";

export interface NationFilters {
  search?: string;
  groupName?: string;
  isEliminated?: boolean;
  page?: number;
  limit?: number;
}

export const nationsService = {
  getAllNations: async (
    filters?: NationFilters,
  ): Promise<{ nations: Nation[]; pagination: Pagination }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("search", filters.search);
    if (filters?.groupName) params.append("groupName", filters.groupName);
    if (filters?.isEliminated !== undefined) params.append("isEliminated", String(filters.isEliminated));
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const query = params.toString();
    return await apiClient.get(
      `${API_ENDPOINTS.NATIONS.LIST}${query ? `?${query}` : ""}`,
    );
  },

  // Get single nation
  getNation: async (id: number): Promise<Nation> => {
    return await apiClient.get(API_ENDPOINTS.NATIONS.DETAIL(id));
  },

  // Create nation
  createNation: async (data: CreateNationRequest): Promise<Nation> => {
    return await apiClient.post(API_ENDPOINTS.NATIONS.CREATE, data);
  },

  // Update nation
  updateNation: async (
    id: number,
    data: UpdateNationRequest,
  ): Promise<Nation> => {
    return await apiClient.patch(API_ENDPOINTS.NATIONS.UPDATE(id), data);
  },

  // Delete nation
  deleteNation: async (id: number): Promise<{ message: string }> => {
    return await apiClient.delete(API_ENDPOINTS.NATIONS.DELETE(id));
  },

  // Eliminate nation
  eliminateNation: async (
    id: number,
    data: EliminateNationRequest,
  ): Promise<{ message: string }> => {
    return await apiClient.post(API_ENDPOINTS.NATIONS.ELIMINATE(id), data);
  },

  // Upload flag image
  uploadFlag: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("logo", file);

    return await apiClient.post(API_ENDPOINTS.NATIONS.UPLOAD_FLAG, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};
